import { Box, Button, Typography, IconButton, Divider, makeStyles, Tooltip } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import AddIcon from '@material-ui/icons/Add';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useSelector } from 'react-redux';
import { ImageViewer, OpacityControl } from '../map';
import { useTranslation } from 'react-i18next';
import { createMarkerIcon } from './utils/markerUtils';
import React from 'react';

import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import SimpleInfoDialog from '../dialog/SimpleInfoDialog';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * MEPBay layer management menu and map overlay.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Mepbay
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    display: 'flex',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
  sidePanel: {
    width: 400,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    padding: theme.spacing(2),
    overflowY: 'auto',
    maxHeight: '100vh',
  },
  stepCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  completedStep: {
    backgroundColor: 'rgba(76,175,80,0.15)',
    borderLeft: '4px solid #4caf50',
  },
  activeStep: {
    backgroundColor: 'rgba(255,152,0,0.15)',
    borderLeft: '4px solid #ff9800',
  },
  inactiveStep: {
    backgroundColor: theme.palette.background.default,
    borderLeft: `4px solid ${theme.palette.divider}`,
  },
  coordinatesText: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  selectingMode: {
    '& *': {
      cursor: 'crosshair !important',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      border: '3px solid #ff9800',
      borderRadius: '4px',
      pointerEvents: 'none',
      zIndex: 1000,
      animation: '$pulse 2s infinite',
    },
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
  layersButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  floatingWindow: {
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: theme.spacing(1),
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    zIndex: 1001,
    maxWidth: '90vw',
    overflow: 'hidden',
  },
  layersWindow: {
    width: '400px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  windowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    borderRadius: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 0`,
  },
  windowContent: {
    padding: theme.spacing(1.5),
    overflowY: 'auto',
    minHeight: 0,
  },
  expandButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
  },
  layerItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  layerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  opacitySlider: {
    width: 80,
    marginLeft: theme.spacing(1),
  },
  activeLayerIndicator: {
    backgroundColor: '#e3f2fd',
    borderLeft: '4px solid #2196f3',
  },
}));

/**
 * @function LayerControler
 * @description Collapsible floating window for managing MEPBay parabolic model layers, with
 * visibility, opacity, deletion, and active-layer switching controls, plus creating new layers.
 * Starts expanded.
 * @param {Object} props - Component props
 * @param {Object} props.layers - Object containing all layers
 * @param {String} props.activeLayerId - ID of the currently active layer
 * @param {Object} props.layersRef - Reference to layers object
 * @param {Object} props.mapRef - Reference to Google Maps instance
 * @param {Function} props.setLayers - Function to update layers state
 * @param {Function} props.setActiveLayerId - Function to set active layer ID
 * @param {Function} props.setLayerCounter - Function to update layer counter
 * @param {Number} props.layerCounter - Current layer counter value
 * @param {Function} props.setControlPoints - Function to set control points
 * @param {Function} props.setCurrentStep - Function to set current step
 * @param {Function} props.setIsSelectingPoint - Function to set point selection state
 * @see {@link createNewLayer}
 * @see {@link switchToLayer}
 * **Called by:
 * @see {ProcessingPage} - MEPBay module's processing page (imported as ProcessingMepBayPage).
 * @memberof module:Mepbay
 * @returns {JSX.Element} The layers management window.
 */
const LayerControler = ({
  layers,
  activeLayerId,
  layersRef,
  mapRef,
  setLayers,
  setActiveLayerId,
  setLayerCounter,
  layerCounter,
  setControlPoints,
  setCurrentStep,
  setIsSelectingPoint,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const images = useSelector((state) => state.imagery.images);
  const { t } = useTranslation();

  /**
   * @function generateLayerColor
   * @description Randomly selects a color from a predefined palette for new layer creation.
   * **Called by:
   * @see {createNewLayer}
   * @memberof module:Mepbay
   * @returns {String} Hex color code.
   */
  const generateLayerColor = () => {
    const colors = [
      '#f44336',
      '#e91e63',
      '#9c27b0',
      '#673ab7',
      '#3f51b5',
      '#2196f3',
      '#03a9f4',
      '#00bcd4',
      '#009688',
      '#4caf50',
      '#8bc34a',
      '#cddc39',
      '#ffeb3b',
      '#ffc107',
      '#ff9800',
      '#ff5722',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  /**
   * @function createNewLayer
   * @description Creates a new layer with default settings (random color, empty control points,
   * step 0) and sets it as the active layer.
   * @see {@link generateLayerColor}
   * **Called by:
   * @see {LayerControler} - via the "New layer" menu item's onClick.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const createNewLayer = () => {
    const newLayerId = `layer_${layerCounter + 1}`;
    const newLayer = {
      id: newLayerId,
      name: `${t('forms.mepbay.result.layerName')} ${layerCounter + 1}`,
      color: generateLayerColor(),
      visible: true,
      opacity: 1.0,
      controlPoints: {
        diffractionPoint: null,
        coastalControlPoint: null,
        predominantWavePoint: null,
      },
      markers: {},
      lines: [],
      completed: false,
    };

    layersRef.current[newLayerId] = newLayer;

    setLayers((prev) => ({ ...prev, [newLayerId]: newLayer }));
    setLayerCounter((prev) => prev + 1);
    setActiveLayerId(newLayerId);
    setControlPoints({
      diffractionPoint: null,
      coastalControlPoint: null,
      predominantWavePoint: null,
    });
    setCurrentStep(0);
    setIsSelectingPoint(false);
  };

  /**
   * @function deleteLayer
   * @description Deletes a layer and removes all its map elements (markers, lines, overlays,
   * ROI polygon, or R0 text overlay), then selects another remaining layer as active (or none).
   * @param {String} layerId - The ID of the layer to delete.
   * **Called by:
   * @see {LayerControler} - via each layer row's delete icon.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const deleteLayer = (layerId) => {
    const layer = layersRef.current[layerId];
    if (layer) {
      if (layer.overlay) {
        layer.overlay.setMap(null);
      } else if (layer.isROI && layer.polygon) {
        layer.polygon.setMap(null);
      } else {
        Object.values(layer.markers).forEach((marker) => marker.setMap(null));
        layer.lines.forEach((line) => line.setMap(null));
        if (layer.r0Overlay) {
          layer.r0Overlay.setMap(null);
          layer.r0Overlay = null;
        }
      }
      delete layersRef.current[layerId];
      setLayers((prev) => {
        const updated = { ...prev };
        delete updated[layerId];
        return updated;
      });

      if (activeLayerId === layerId) {
        const remainingLayers = Object.keys(layersRef.current).filter((id) => !layersRef.current[id].isROI);
        if (remainingLayers.length > 0) {
          setActiveLayerId(remainingLayers[0]);
          setControlPoints(layersRef.current[remainingLayers[0]].controlPoints);
        } else {
          setActiveLayerId(null);
          setControlPoints({
            diffractionPoint: null,
            coastalControlPoint: null,
            predominantWavePoint: null,
          });
        }
      }
    }
  };

  /**
   * @function toggleLayerVisibility
   * @description Toggles the visibility of a layer and all its associated elements on the map
   * (overlay, ROI polygon, or markers/lines/R0 overlay).
   * @param {String} layerId - The ID of the layer to toggle.
   * **Called by:
   * @see {LayerControler} - via each layer row's visibility icon.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const toggleLayerVisibility = (layerId) => {
    const layer = layersRef.current[layerId];
    if (layer) {
      layer.visible = !layer.visible;

      if (layer?.overlay) {
        layer.overlay.setMap(layer.visible ? mapRef.current : null);
      } else if (layer.isROI && layer.polygon) {
        layer.polygon.setVisible(layer.visible);
      } else {
        Object.values(layer?.markers).forEach((marker) => {
          marker.setVisible(layer?.visible);
        });
        layer?.lines?.forEach((line) => {
          line.setVisible(layer.visible);
        });
        if (layer.r0Overlay) {
          layer.r0Overlay.setVisible(layer.visible);
        }
      }
      setLayers((prev) => ({
        ...prev,
        [layerId]: { ...prev[layerId], visible: layer.visible },
      }));
    }
  };

  /**
   * @function updateLayerOpacity
   * @description Updates the opacity of a layer and all its visual elements: the overlay/ROI
   * polygon, each line (handling regular strokes, filled arcs, and dashed "red lines"
   * differently), and each marker (re-encoding the SVG icon for angle-text markers, or rebuilding
   * the icon via `createMarkerIcon` for point markers).
   * @param {String} layerId - The ID of the layer to update.
   * @param {Number} opacity - The new opacity value (0-1).
   * @see {@link createMarkerIcon}
   * **Called by:
   * @see {LayerControler} - via each layer row's opacity slider.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const updateLayerOpacity = (layerId, opacity) => {
    const layer = layersRef.current[layerId];
    if (layer) {
      layer.opacity = opacity;
      if (layer?.overlay) {
        layer.overlay.setOpacity(opacity);
      } else if (layer.isROI && layer.polygon) {
        layer.polygon.setOptions({
          fillOpacity: 0.2 * opacity,
          strokeOpacity: 0.6 * opacity,
        });
      } else {
        layer.lines.forEach((line) => {
          if (line.fillOpacity !== undefined) {
            line.setOptions({
              strokeOpacity: opacity * 0.8,
              fillOpacity: Math.min(opacity, 0.1),
            });
          } else if (line.redLine && line.icons) {
            line.setOptions({
              strokeOpacity: 0,
              icons: [
                {
                  icon: {
                    path: 'M 0,-1 0,1',
                    strokeOpacity: opacity * 0.7,
                    strokeColor: layer.color,
                    scale: 4,
                  },
                  offset: '0',
                  repeat: '20px',
                },
              ],
            });
          } else {
            line.setOptions({
              strokeOpacity: opacity,
            });
          }
        });

        Object.entries(layer.markers).forEach(([pointKey, marker]) => {
          if (marker && marker.setIcon) {
            if (pointKey === 'betaAngle' || pointKey === 'angleCA') {
              try {
                const currentIcon = marker.getIcon();

                if (currentIcon && currentIcon.url && currentIcon.url.includes('svg')) {
                  let decodedSvg = decodeURIComponent(currentIcon.url.replace('data:image/svg+xml;charset=UTF-8,', ''));

                  if (decodedSvg.includes('opacity=')) {
                    decodedSvg = decodedSvg.replace(/opacity="[^"]*"/g, `opacity="${opacity}"`);
                  } else {
                    decodedSvg = decodedSvg.replace(/<text([^>]*)>/g, `<text$1 opacity="${opacity}">`);
                  }

                  const updatedSvg = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(decodedSvg);

                  marker.setIcon({
                    ...currentIcon,
                    url: updatedSvg,
                  });
                }
              } catch (error) {
                console.log('Could not update angle marker opacity:', error);
              }
              return;
            }

            const newIcon = createMarkerIcon(pointKey, layer, opacity);
            if (newIcon) {
              marker.setIcon(newIcon);
            }
          }
        });

        if (layer.r0Overlay && layer.r0Overlay.setOpacity) {
          layer.r0Overlay.setOpacity(opacity);
        }
      }
      setLayers((prev) => ({
        ...prev,
        [layerId]: { ...prev[layerId], opacity },
      }));
    }
  };

  /**
   * @function switchToLayer
   * @description Switches the active layer, restoring its control points and resuming the
   * wizard at the appropriate step (based on which control points it already has, capped at
   * step 2).
   * @param {String} layerId - The ID of the layer to switch to. Ignored for ROI layers.
   * **Called by:
   * @see {LayerControler} - via each layer row's onClick.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const switchToLayer = (layerId) => {
    if (layersRef.current[layerId] && !layersRef.current[layerId].isROI) {
      setActiveLayerId(layerId);
      setControlPoints(layersRef.current[layerId].controlPoints);
      const layer = layersRef.current[layerId];
      let nextStep = 0;
      if (layer.controlPoints.diffractionPoint) nextStep = 1;
      if (layer.controlPoints.coastalControlPoint) nextStep = 2;
      if (layer.controlPoints.predominantWavePoint) nextStep = 3;

      setCurrentStep(nextStep >= 3 ? 2 : nextStep);
      setIsSelectingPoint(false);
    }
  };

  const [newLayerHelp, setNewLayerHelp] = React.useState(false);
  const [layersWindowExpanded, setLayersWindowExpanded] = React.useState(true);

  return (
    <div className={`${classes.floatingWindow} ${classes.layersWindow}`}>
      <div className={classes.windowHeader}>
        <Box display="flex" alignItems="center">
          <Typography variant="subtitle1" style={{ marginRight: 8, fontWeight: 500 }}>
            {t('forms.mepbay.result.layers')}
          </Typography>
          <Tooltip title={t('forms.mepbay.result.createNewLayer')}>
            <IconButton onClick={() => setNewLayerHelp(true)} size="small" style={{ padding: 4 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <SimpleInfoDialog
          open={newLayerHelp}
          close={() => setNewLayerHelp(false)}
          title="forms.mepbay.result.createNewLayer"
          text="forms.mepbay.result.createNewLayerInfo"
        />

        <IconButton className={classes.expandButton} onClick={() => setLayersWindowExpanded(!layersWindowExpanded)}>
          {layersWindowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>

      {layersWindowExpanded && (
        <div className={classes.windowContent}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={createNewLayer}
            fullWidth>
            {t('forms.mepbay.result.createNewLayer')}
          </Button>

          {Object.keys(layers).length > 0 && <Divider style={{ margin: '8px 0' }} />}

          {Object.values(layers).map((layer) => (
            <Box key={layer.id}>
              <Box
                style={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  borderRadius: 4,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  margin: '4px 0',
                  overflow: 'hidden',
                  border: activeLayerId === layer.id ? '2px solid #2196f3' : `1px solid ${theme.palette.divider}`,
                }}>
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '2px 8px',
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}>
                  <Box onClick={() => switchToLayer(layer.id)} style={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      style={{ cursor: 'pointer', height: '100%' }}
                      width={12}
                      height={12}
                      borderRadius="50%"
                      bgcolor={layer.color}
                      marginRight={1}
                    />
                    <Typography
                      variant="body2"
                      style={{
                        cursor: 'pointer',
                        fontWeight: activeLayerId === layer.id ? 'bold' : 'normal',
                      }}>
                      {layer.name}
                    </Typography>
                    {layer.completed && <CheckCircleIcon style={{ marginLeft: 6, color: '#4caf50', fontSize: 16 }} />}
                  </Box>

                  <Box style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => switchToLayer(layer.id)}
                      color={activeLayerId === layer.id ? 'primary' : 'default'}>
                      <Box
                        width={8}
                        height={8}
                        borderRadius="50%"
                        bgcolor={activeLayerId === layer.id ? '#2196f3' : '#ccc'}
                      />
                    </IconButton>

                    <IconButton size="small" onClick={() => toggleLayerVisibility(layer.id)}>
                      {layer.visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                    </IconButton>
                    <OpacityControl
                      size="small"
                      opacity={layer.opacity}
                      onOpacityChange={(v) => updateLayerOpacity(layer.id, v)}
                    />
                    <IconButton size="small" onClick={() => deleteLayer(layer.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}

          {Object.keys(images)
            .reverse()
            .map((id) => (
              <ImageViewer withExpression={false} key={id} index={id} image={images[id]} />
            ))}
        </div>
      )}
    </div>
  );
};

export default LayerControler;
